import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useActivity } from '../state/ActivityProvider';
import TopbarInner from "../components/topbar/TopbarInner";

const toISO = (d) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10); // YYYY-MM-DD en heure locale
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const FR_MONTHS = [
  'janvier','février','mars','avril','mai','juin',
  'juillet','août','septembre','octobre','novembre','décembre'
];
const FR_WEEK = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

export default function CalendarPage({
  onThemeToggle,
  isPremium = false,
  longestStreak = 0,
  title = "Calendrier",
}) {
  const navigate = useNavigate();
  const { activity, isHydrating } = useActivity();

  const [monthRef, setMonthRef] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedISO, setSelectedISO] = useState(() => toISO(new Date()));
  const todayISO = toISO(new Date());

  // ✅ Reconstruire byDay directement depuis activity
  const { byDay } = useMemo(() => {
    const map = new Map();

    for (const e of activity) {
      const key = e.dateISO;
      if (!key) continue;

      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }

    return { byDay: map };
  }, [activity]);

  const days = useMemo(() => buildMonthGrid(monthRef), [monthRef]);
  const selectedEntries = byDay.get(selectedISO) || [];

  return (
    <>
      <TopbarInner
        title={title}
        onBack={() => navigate(-1)}
        onThemeToggle={onThemeToggle}
        titleColor="#36495B"
        showLogo={false}
        isPremium={isPremium}
        longestStreak={longestStreak}
      />

      <div className="cal-page app-safe--no-top">
        {/* HEADER mois + flèches */}
        <div className="cal-header">
          <div className="month-nav">
            <button
              className="nav-icon"
              onClick={() => setMonthRef(m => addMonths(m, -1))}
            >
              <ChevronLeft size={18} aria-hidden />
            </button>
            <div className="month-title" aria-live="polite">
              {FR_MONTHS[monthRef.getMonth()]} {monthRef.getFullYear()}
            </div>
            <button
              className="nav-icon"
              onClick={() => setMonthRef(m => addMonths(m, 1))}
            >
              <ChevronRight size={18} aria-hidden />
            </button>
          </div>
        </div>

        {/* LÉGENDE */}
        <div className="legend">
          <span className="d d-listen" title="Écoute de sons">Écoute</span>
          <span className="d d-ressenti" title="Ressenti saisi">Ressenti</span>
        </div>

        {/* CALENDRIER */}
        <div className="grid">
          {FR_WEEK.map((w) => (
            <div key={w} className="cell head">{w}</div>
          ))}

          {days.map((cell) => {
            const iso = toISO(cell.date);
            const entries = byDay.get(iso) || [];
            const isToday = iso === todayISO;
            const inMonth = cell.inMonth;
            const types = new Set(entries.map(e => e.type));

return (
  <button
    key={iso}
    className={[
      'cell',
      'day',
      inMonth ? '' : 'muted',
      isToday ? 'today' : '',
      selectedISO === iso ? 'selected' : '',
    ].join(' ')}
    onClick={() => setSelectedISO(iso)}
    title={buildTitleTooltip(types, entries)}
  >
    <span className="num">{cell.date.getDate()}</span>

    {/* ✅ On garde les pastilles pour écoute / ressenti */}
    <div className="dots">
      {['listen', 'ressenti'].map((t) =>
        types.has(t) ? <span key={t} className={`dot ${t}`} /> : null
      )}
    </div>

    {/* ❌ On ENLÈVE complètement l’affichage des minutes dans la case */}
    {/* plus de :
    */}
  </button>
);
          })}
        </div>

        {/* DÉTAILS DU JOUR */}
        <div className="details">
<h2>
  {formatISOToFr(selectedISO)} —{" "}
  {isHydrating
    ? "Synchronisation..."
    : selectedEntries.length > 0
      ? `${selectedEntries.length} élément(s)`
      : "Aucune activité"}
</h2>
{isHydrating && (
  <div className="sync-banner">
    Synchronisation de vos écoutes et ressentis sur votre compte…
  </div>
)}

{selectedEntries.length > 0 && (
  <div className="list">
    {selectedEntries.map((e, idx) => (
      <div key={idx} className="item">
        <div className="badge" data-type={e.type}>
          {labelType(e.type)}
        </div>

        <div className="content">
          {e.type === "ressenti" ? (
            // ✅ CAS RESSENTI : on affiche juste "• message" sur une seule ligne
            <div className="line">
              <span className="sep">•</span>
              {e.content && (
                <span className="note">{e.content}</span>
              )}
            </div>
          ) : (
            // ✅ CAS ACTIVITÉ : on garde l'affichage minutes + libellé + éventuellement une note dessous
            <>
<>
  <div className="line">
    <span className="sep">•</span>
    {e.minutes ? (
      <strong>{formatMinutes(e.minutes)}</strong>
    ) : (
      <em>—</em>
    )}
  </div>
{getActivityLabel(e) && (
  <div className="track-name">{getActivityLabel(e)}</div>
)}
</>
            </>
          )}
        </div>
      </div>
    ))}
  </div>
)}
        </div>

        {/* STYLES */}
        <style>{`
.cal-page {
  padding: 40px 20px 80px;
  margin-top: 55px; /* espace sous la TopbarInner */
  display: flex;
  flex-direction: column;
  align-items: center;
  background: transparent;
  position: relative;
  z-index: 1;
  font-family: var(--font-sans);
  animation: fadeIn 0.6s ease;
}
.cal-header {
  width: 100%;
  max-width: 900px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
}
.month-nav { display: inline-flex; align-items: center; gap: 8px; }
.nav-icon {
  width: 34px; height: 34px; border-radius: 10px; border: 1px solid #e5e7eb;
  background: white; cursor: pointer;
}
.month-title { font-weight: 700; color: #1f2937; min-width: 180px; text-align: center; }

.legend {
  width: 100%; max-width: 900px;
  display: flex; gap: 16px; align-items: center; color: #4b5563; margin: 8px 0 14px;
  flex-wrap: wrap;
}
.legend .d { display: inline-flex; align-items: center; gap: 6px; font-size: 0.95rem; }
.legend .d:before { content: ''; display:inline-block; width:10px; height:10px; border-radius:9999px; }
.legend .d-listen:before { background:#60a5fa; }
.legend .d-ressenti:before { background:#a78bfa; }

.grid {
  width: 100%; max-width: 900px;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  background: rgba(255,255,255,0.6);
  padding: 12px;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.08);
}
.cell {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  min-height: 92px;
  padding: 8px;
}
.cell.head {
  background: transparent;
  border: none;
  min-height: auto;
  padding: 4px 8px;
  color: #6b7280;
  font-weight: 600;
  text-align: center;
}
.cell.day {
  display: flex; flex-direction: column; align-items: flex-start;
  position: relative; text-align: left; cursor: pointer;
  transition: transform .12s ease, box-shadow .2s ease, border-color .2s ease;
}
.cell.day:hover { transform: translateY(-2px); box-shadow: 0 8px 18px rgba(99,102,241,0.12); border-color:#c7d2fe; }
.cell.day.muted { opacity: 0.45; }
.cell.day.today { box-shadow: 0 0 0 2px #a5b4fc inset; }
.cell.day.selected { outline: 2px solid #6366f1; outline-offset: 2px; }

.num { font-weight: 700; color: #111827; }

.dots {
  position: absolute;
  left: 8px;
  bottom: 6px;
  display: flex;
  gap: 4px;
  align-items: center;
}
.dot {
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  display: inline-block;
}
.dot.listen { background: #60a5fa; }   /* bleu écoute */
.dot.ressenti { background: #a78bfa; } /* violet ressenti */

.grid .cell.day .dots .dot.ressenti {
  width: 7px !important;
  height: 7px !important;
  border-radius: 9999px !important;
  display: inline-block !important;
  background: #a78bfa !important;
  transform: none !important;
  scale: 1 !important;
  margin: 0 !important;
  padding: 0 !important;
}

.details { width: 100%; max-width: 900px; margin-top: 18px; }
.details h2 { color: #1f2937; font-size: 1.1rem; margin-bottom: 10px; }
.sync-banner {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  font-size: 0.95rem;
}
.list { display: grid; gap: 10px; }
.item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255,255,255,0.9);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 14px;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  padding: 6px 10px;
  border-radius: 9999px;
  font-size: .85rem;
  font-weight: 600;
  color: #111827;
  line-height: 1;
}
.content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
  .line {
  display: flex;
  align-items: center;
  gap: 6px;
}
  
.badge[data-type="listen"] { background:#dbeafe; }
.badge[data-type="practice"] { background:#d1fae5; }
.badge[data-type="breath"] { background:#fef3c7; }
.badge[data-type="ressenti"] { background:#ede9fe; }
.content .line {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #374151;
  min-width: 0;
  flex-wrap: nowrap;
}
.note { color:#4b5563; margin-top: 2px; white-space: pre-wrap; }
.line .note {
  margin-top: 0; /* ✅ pas de décalage quand la note est dans la ligne */
}

.sep--dim {
  color: #9ca3af;
  font-weight: 600;
}

.track-name {
  color: #6b7280;
  font-weight: 600;
  font-size: 0.88rem;
  margin-top: 2px;
  line-height: 1.3;
  word-break: break-word;
  padding-left: 14px; /* aligne avec la durée (largeur du "• " + gap) */
}
  
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 767px) {
  .cal-page {
    padding: 24px 12px 60px;
    max-width: 100vw;
    overflow-x: hidden;
    margin-top: 82px; /* 🔥 espace supplémentaire uniquement sur mobile */
  }

  .cal-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .month-nav {
    justify-content: center;
  }

  .legend {
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    text-align: center;
  }

  .grid {
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 4px;
    padding: 8px;
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
  }

  .grid::-webkit-scrollbar {
    height: 6px;
  }
  .grid::-webkit-scrollbar-thumb {
    background: rgba(99,102,241,0.3);
    border-radius: 10px;
  }

  .cell {
    min-height: 68px;
    font-size: 0.9rem;
  }

  .month-title {
    min-width: auto;
    font-size: 1.05rem;
  }

  .details {
    margin-top: 14px;
    padding: 0 6px;
  }
}
        `}</style>
      </div>
    </>
  );
}

/** Construit la grille du mois (commence lundi) */
function buildMonthGrid(ref) {
  const first = startOfMonth(ref);
  const last = endOfMonth(ref);

  // JS: 0=Dim... on veut Lundi=0
  const firstWeekday = (first.getDay() + 6) % 7; // 0..6
  const prefix = firstWeekday;

  const cells = [];
  // jours du mois précédent pour remplir la première semaine
  for (let i = prefix - 1; i >= 0; i--) {
    const d = new Date(first);
    d.setDate(first.getDate() - (i + 1));
    cells.push({ date: d, inMonth: false });
  }
  // jours du mois courant
  for (let day = 1; day <= last.getDate(); day++) {
    const d = new Date(ref.getFullYear(), ref.getMonth(), day);
    cells.push({ date: d, inMonth: true });
  }
  // compléter jusqu’à 42 cellules (6 semaines)
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const lastD = cells[cells.length - 1].date;
    const d = new Date(lastD);
    d.setDate(lastD.getDate() + 1);
    cells.push({ date: d, inMonth: false });
  }
  return cells;
}

function formatISOToFr(iso) {
  try {
    const [y, m, d] = iso.split('-').map(Number);
    return `${d} ${FR_MONTHS[m - 1]} ${y}`;
  } catch {
    return iso;
  }
}

function buildTitleTooltip(types, entries) {
  const labels = [];
  if (types.has('listen')) labels.push('Écoute');
  if (types.has('ressenti')) labels.push('Ressenti');
  if (!labels.length && entries.length) labels.push(`${entries.length} élément(s)`);
  return labels.join(' • ');
}

function formatMinutes(min) {
  if (min == null || isNaN(min)) return "—";
  const totalSeconds = Math.round(min * 60);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) return `${seconds} sec`;
  if (seconds === 0) return `${minutes} min`;
  return `${minutes} min ${seconds} sec`;
}

function getActivityLabel(entry) {
  // Session IA : on construit le libellé à partir du goal
  if (entry?.meta?.sessionType === "ai-composed" || entry?.meta?.trackId?.startsWith("ai:")) {
    const goal = entry?.meta?.trackId?.split(":")[1];
    switch (goal) {
      case "relax":
        return "Détente Auria";
      case "rest":
        return "Repos Auria";
      case "recenter":
        return "Recentrage Auria";
      default:
        return "Session IA";
    }
  }

  // Session classique : on garde le nom du morceau (Cohérence Cardiaque, etc.)
  return entry?.content || null;
}

function labelType(t) {
  switch (t) {
    case 'listen':
      return 'Écoute';
    case 'ressenti':
      return 'Ressenti';
    case 'practice':
      return 'Pratique';
    case 'breath':
      return 'Respiration';
    default:
      return t;
  }
}